import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { ContentType } from "@/lib/backend/api";
import { logger } from "@/logger";

/** Wat de server terugstuurt nadat de keuze is vastgelegd. */
export type AppVersionState = {
  stage: string;
  choice: boolean | null;
  enabled: boolean;
  switchable: boolean;
};

/**
 * Welke helft van de app deze medewerker wil zien.
 *
 * Stond tot voor kort in de opslag van het toestel. Daar was hij per apparaat verschillend,
 * onzichtbaar voor het beheer en niet te overrulen -- en die drie dingen zijn precies wat een
 * uitrol nodig heeft. Nu hangt hij aan het lidmaatschap op de server.
 *
 * `null` betekent "volg de organisatie weer" en is iets anders dan `false`: alleen wie op `null`
 * staat beweegt mee als de uitrol een sport hoger gaat.
 *
 * Met de hand en niet via de gegenereerde client: die komt uit de OpenAPI-beschrijving van de API
 * en kent dit endpoint pas na de eerstvolgende `generate:api`. Zodra dat gebeurd is mag deze
 * aanroep vervangen worden door `api.me.updateAppVersion`.
 */
export function useUpdateAppVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (useNewApp: boolean | null) => {
      const response = await api.request<AppVersionState>({
        path: "/me/app-version",
        method: "PATCH",
        body: { useNewApp },
        type: ContentType.Json,
        format: "json",
      });

      return response.data;
    },
    onSuccess: () => {
      // De vlaggen dragen de uitkomst, dus die moeten opnieuw opgehaald worden -- de app tekent
      // zich daarna om. Niet zelf in de cache schrijven: dan rekent de app de som na, en dat is
      // precies de tweede uitwerking die uit de pas gaat lopen.
      void queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
    },
    onError: (error) => {
      logger.error({ error }, "Appversie wisselen mislukte");
    },
  });
}
